import { Location } from "../../models/location";
import { QualityAlertLocationInfo } from "../../models/quality-alert";
import { Location as TQSLocation } from "../../models/location";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch } from "../../reducers/store";
import { getLocationById as getLocationByIdAction } from "../../reducers/supplierSlice";

type Status =
  | "Issuing Location"
  | "Checking For Issue"
  | "Issue Found"
  | "No Issue"
  | "Parts not sent here"
  | "New";

interface StatusColorClasses {
  background: string;
  border: string;
  text: string;
}

const statusColors: Record<Status, StatusColorClasses> = {
  "Issuing Location": {
    background: "bg-tqsLightRed",
    border: "border-tqsRed",
    text: "text-tqsRed",
  },
  "Checking For Issue": {
    background: "bg-tqsLightBlue",
    border: "border-tqsBlue",
    text: "text-tqsBlue",
  },
  "Issue Found": {
    background: "bg-tqsLightRed",
    border: "border-tqsRed",
    text: "text-tqsRed",
  },
  "No Issue": {
    background: "bg-tqsLightGreen",
    border: "border-tqsGreen",
    text: "text-tqsGreen",
  },
  New: {
    background: "bg-tqsLightBlue",
    border: "border-tqsBlue",
    text: "text-tqsBlue",
  },
  "Parts not sent here": {
    background: "bg-[#00000020]",
    border: "border-gray",
    text: "text-gray-700",
  },
};

const defaultColorClasses: StatusColorClasses = {
  background: "bg-gray-100",
  border: "border-gray-300",
  text: "text-gray-700",
};

const borderToBackgroundColor: { [key: string]: string } = {
  "border-tqsRed": "bg-tqsRed",
  "border-tqsBlue": "bg-tqsBlue",
  "border-tqsYellow": "bg-tqsYellow",
  "border-tqsGreen": "bg-tqsGreen",
  "border-orange-300": "bg-orange-300",
  "border-gray": "bg-gray-300",
  // Add other mappings as necessary
};

const statusOrderMap = new Map<string, number>([
  ["Issuing Location", 0],
  ["Issue Found", 1],
  ["New", 2],
  ["Checking For Issue", 3],
  ["No Issue", 4],
  ["Parts not sent here", 5],
]);

const BreathingCircle: React.FC<{ color: string }> = ({ color }) => {
  return (
    <div className="absolute top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 transform">
      <div className="relative h-4 w-4">
        <div className={`wave-ring ${color}`} />
        <div className={`wave-ring ${color}`} />
        <div className={`wave-ring ${color}`} />
      </div>
    </div>
  );
};

const supplierIconUrl =
  "https://s3.amazonaws.com/com.teamqualityservices.qnet/images/Logo+Q+Only.png";

const LocationCard: React.FC<
  QualityAlertLocationInfo & { isLast: boolean; isSuggested: boolean }
> = ({ location, status, isLast, isSuggested }) => {
  const dispatch = useAppDispatch();
  const colorClasses = statusColors[status as Status] || defaultColorClasses;
  const breathingCircleColor = borderToBackgroundColor[colorClasses.border];
  const locationObj = typeof location === 'number' ? null : (location as unknown as TQSLocation);
  const locationImageUrl = locationObj?.oemPicture?.s3Code || supplierIconUrl;

  return (
    <div className="relative pl-8">
      {!isSuggested ? (
        <div className="absolute left-4 top-1/2 h-px w-4 bg-gray-300"></div>
      ) : (
        <div className="absolute left-4 top-1/2 h-px w-4 border-t-[1px] border-dashed border-gray-300"></div>
      )}
      <div
        className={`mb-2 flex h-full w-full items-center justify-between rounded-lg border-2 p-[8px] pl-[13∂px] text-black ${colorClasses.border} shadow-lg ${status === "Issuing Location" ? colorClasses.background : isSuggested ? "bg-[#edeeee]" : ""} ${isSuggested ? "border-dashed" : ""}`}
        style={{ zIndex: 1 }}
      >
        <div className="flex items-center">
          <div
            className={`mr-3 rounded-full border-3 bg-white p-2 ${colorClasses.border} flex items-center justify-center`}
            style={{
              height: "40px",
              width: "40px",
              minWidth: "40px",
              minHeight: "40px",
              aspectRatio: 1,
            }}
          >
            <img
              className="h-[17px] w-[18px] object-contain"
              src={locationImageUrl}
              alt="OEM Image"
            />
          </div>

          <div className="">
            <h3 className="mr-4 text-xs font-bold text-[#212322]">
              {locationObj?.name ?? 'Unknown Location'}
            </h3>
            <div className="flex flex-col">
              <p className="text-xs text-gray-600">
                {locationObj?.oem?.name ?? 'Unknown OEM'}
              </p>
            </div>
          </div>
        </div>
        {!isSuggested ? (
          <div
            className={`h-full w-[140px] min-w-[140px] max-w-[140px] overflow-hidden whitespace-nowrap rounded-md px-3 py-1 text-center text-[14px] font-semibold text-tqsBlack ${colorClasses.text} ${status === "Issuing Location" ? "bg-red-300" : `${colorClasses.background} `}`}
          >
            {status === "Checking For Issue" || status === "New"
              ? "Investigating"
              : status}
          </div>
        ) : (
          <a
            target="_blank"
            href={`https://enspire-qnet.qlever.io/orders?location=${locationObj?.id ?? ''}`}
          >
            <div
              className={`flex h-full min-w-[140px] max-w-[180px] items-center overflow-hidden whitespace-nowrap rounded-[50px] border-[1px] border-tqsBlack bg-[#afb1b0] pr-3 text-center text-[14px] font-semibold text-tqsBlack`}
            >
              <FontAwesomeIcon
                className="text-[25px] text-tqsBlack"
                icon={faCirclePlus}
              />
              <span className="pl-1 text-tqsBlack">Add Liaison Coverage</span>
            </div>
          </a>
        )}
      </div>
      <BreathingCircle color={breathingCircleColor} />
    </div>
  );
};

interface QualityAlertLocationCardsProps {
  qualityAlertLocationInfo: QualityAlertLocationInfo[];
  selectedLocationId: number;
  allLocations: TQSLocation[];
  issueNumber: string;
  suggestedLocations: number[];
}

const QualityAlertLocationCards: React.FC<QualityAlertLocationCardsProps> = ({
  qualityAlertLocationInfo,
  selectedLocationId,
  allLocations,
  issueNumber,
  suggestedLocations,
}) => {
  const dispatch = useAppDispatch();
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerTop, setContainerTop] = useState(0);
  const [locationSuggest, setLocationSuggest] = useState<any[]>([]);

  useEffect(() => {
    getHeight();
    getTop();
    window.addEventListener("resize", () => {
      getHeight();
      getTop();
    });
  }, []);

  useEffect(() => {
    getHeight();
    getTop();
  }, [locationSuggest]);

  useEffect(() => {
    createLocationSuggest();
  }, [suggestedLocations]);
  const getLocationById = async (locationId: number) => {
    try {
      const result = await dispatch(getLocationByIdAction(locationId));
      if (result.payload) {
        return result.payload;
      }
      throw new Error('Failed to get location data');
    } catch (error) {
      console.error("Error fetching location by ID:", error);
      return null;
    }
  };
  // Clone the location info to avoid mutating the original array
  const updatedLocationInfo = [...qualityAlertLocationInfo];

  // Update status of the selected location and move it to the top
  const sortedLocationInfo = updatedLocationInfo.map((info) => {
    if ((info.location as unknown as Location)?.id === selectedLocationId) {
      return { ...info, status: "Issuing Location" };
    }
    return info;
  });

  sortedLocationInfo.sort((a, b) => {
    if (a?.locationId === selectedLocationId)
      return -1;
    if (b?.locationId === selectedLocationId)
      return 1;
    return 0;
  });

  sortedLocationInfo.forEach((location) => {});

  var additionalLocations = allLocations.filter(
    (location) =>
      !sortedLocationInfo.some(
        (info) => info?.locationId === location.id,
      ),
  );

  additionalLocations.forEach((location: any) => {
    location.status = "Parts not sent here";
  });

  //DO the Logic here for suggested Locatins

  const createLocationSuggest = async () => {
    const tempLocationSuggest: any[] = [];

    for (let suggestedLocation of (suggestedLocations as any)?.suggestedLocation ?? []) {
      let existsInSorted = false;
      let existsInAdditional = false;
      
      for (let location of sortedLocationInfo) {
        if (location.locationId === suggestedLocation) {
          existsInSorted = true;
          break;
        }
      }
      
      if (!existsInSorted) {
        for (let location of additionalLocations) {
          if (location.id === suggestedLocation) {
            existsInAdditional = true;
            break;
          }
        }
      }

      if (!existsInSorted && !existsInAdditional) {
        const locationData = await getLocationById(suggestedLocation);
        if (locationData) {
          tempLocationSuggest.push(locationData);
        }
      }
    }
    setLocationSuggest(tempLocationSuggest);
  };

  const getHeight = () => {
    const container = document.getElementById(`cardContainer#${issueNumber}`);
    if (container) {
      const firstChild = container.children[0] as HTMLElement;
      const lastChild = container.children[
        container.children.length - 1
      ] as HTMLElement;
      const constainerHeight =
        container.offsetHeight -
        (firstChild.offsetHeight / 2 + lastChild.offsetHeight / 2);

      setContainerHeight(constainerHeight);
    }
  };

  const getTop = () => {
    const container = document.getElementById(`cardContainer#${issueNumber}`);
    if (container) {
      const firstChild = container.children[0] as HTMLElement;
      setContainerTop(firstChild.offsetHeight / 2);
    }
  };
  // sortedLocationInfo.push(...additionalLocations as any[]);

  // Transform sortedLocationInfo to include full location data
  const transformedLocationInfo = sortedLocationInfo.map((info) => {
    // Handle case where location is already an object
    if (typeof info.location === "object" && info.location !== null) {
      return info;
    }

    // Handle case where location is an ID
    const fullLocation = allLocations.find(
      (loc) => loc.id === (info.location as number),
    );

    return {
      ...info,
      location: fullLocation || info.location,
    };
  });

  return (
    <div className="relative space-y-0">
      <div
        className="absolute left-4 w-px bg-gray-300"
        style={{ height: containerHeight, top: containerTop }}
      ></div>
      <div
        className="absolute top-1/2 h-px w-4 bg-gray-300"
        style={{ top: containerTop }}
      ></div>
      <div id={`cardContainer#${issueNumber}`}>
        {transformedLocationInfo
          .sort((a, b) => {
            return (
              (statusOrderMap.get(a?.status ?? "")?.valueOf() ?? 9999) -
              (statusOrderMap.get(b?.status ?? "")?.valueOf() ?? 9999)
            );
          })
          .map((locationInfo, index) => (
            <LocationCard
              key={index}
              {...locationInfo}
              isLast={
                index === transformedLocationInfo.length - 1 &&
                additionalLocations.length === 0
              }
              isSuggested={false}
            />
          ))}

        {additionalLocations.map((location: any, index) => (
          <LocationCard
            key={index}
            {...{ ...location, location: location }}
            isLast={index === additionalLocations.length - 1}
          />
        ))}

        {locationSuggest.map((location: any, index) => (
          <LocationCard
            key={index}
            {...{ ...location, location: location }}
            isLast={index === additionalLocations.length - 1}
            isSuggested={true}
          />
        ))}
      </div>
    </div>
  );
};

export default QualityAlertLocationCards;
